import { useTranslation } from '@kostbar/i18n'
import {
  ROUTING_ROLES,
  ROUTING_ROLE_LABEL_KEYS,
  normalizeRoutingRole,
} from './edge-settings-utils'
import type {
  EdgeDeviceConfig,
  EdgeDeviceRoutingSettings,
  EdgeKioskConfig,
  EdgeRoutingRole,
} from '@/api/types'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'

type RoutingTabProps = {
  normalizedKiosks: Array<EdgeKioskConfig>
  devices: Array<EdgeDeviceConfig>
  zoneOptions: Array<string>
  normalizedDeviceRouting: Required<EdgeDeviceRoutingSettings>
  initialDeviceRouting: Required<EdgeDeviceRoutingSettings>
  unsavedDeviceCount: number
  onSetDeviceRouting: React.Dispatch<
    React.SetStateAction<Required<EdgeDeviceRoutingSettings>>
  >
}

const REQUIRED_ROLES: ReadonlySet<string> = new Set([
  'payment.card',
  'printer.receipt',
])

export function RoutingTab({
  normalizedKiosks,
  devices,
  zoneOptions,
  normalizedDeviceRouting,
  initialDeviceRouting,
  unsavedDeviceCount,
  onSetDeviceRouting,
}: RoutingTabProps) {
  const { t: tRestaurants } = useTranslation('restaurants')

  // ── Derive lookup maps ──

  const assignableDevicesByRole: Record<
    EdgeRoutingRole,
    Array<{ id: string; label: string }>
  > = (() => {
    const result: Record<
      EdgeRoutingRole,
      Array<{ id: string; label: string }>
    > = {
      'payment.card': [],
      'payment.cash_recycler': [],
      'printer.receipt': [],
      'printer.kitchen.hot': [],
    }
    for (const device of devices) {
      const deviceId = typeof device.id === 'string' ? device.id.trim() : ''
      const role = normalizeRoutingRole(device.role)
      if (!deviceId || !role) continue
      const label = (device.name || '').trim() || `${role} #${deviceId}`
      result[role].push({ id: deviceId, label })
    }
    for (const role of ROUTING_ROLES) {
      result[role] = result[role].sort((a, b) => a.label.localeCompare(b.label))
    }
    return result
  })()

  const routingStationMap = new Map<string, string>()
  for (const item of normalizedDeviceRouting.stationAssignments) {
    routingStationMap.set(`${item.stationId}::${item.role}`, item.deviceId)
  }

  const routingZoneMap = new Map<string, string>()
  for (const item of normalizedDeviceRouting.zoneDefaults) {
    routingZoneMap.set(`${item.zone}::${item.role}`, item.deviceId)
  }

  const routingFallbackMap = new Map<EdgeRoutingRole, string>()
  for (const item of normalizedDeviceRouting.fallbacks) {
    routingFallbackMap.set(item.role, item.deviceId)
  }

  // ── Change tracking ──

  const initialRoutingMap = new Map<string, string>()
  for (const item of initialDeviceRouting.stationAssignments) {
    initialRoutingMap.set(
      `station::${item.stationId}::${item.role}`,
      item.deviceId,
    )
  }
  for (const item of initialDeviceRouting.zoneDefaults) {
    initialRoutingMap.set(`zone::${item.zone}::${item.role}`, item.deviceId)
  }
  for (const item of initialDeviceRouting.fallbacks) {
    initialRoutingMap.set(`fallback::${item.role}`, item.deviceId)
  }

  const currentRoutingMap = new Map<string, string>()
  for (const item of normalizedDeviceRouting.stationAssignments) {
    currentRoutingMap.set(
      `station::${item.stationId}::${item.role}`,
      item.deviceId,
    )
  }
  for (const item of normalizedDeviceRouting.zoneDefaults) {
    currentRoutingMap.set(`zone::${item.zone}::${item.role}`, item.deviceId)
  }
  for (const item of normalizedDeviceRouting.fallbacks) {
    currentRoutingMap.set(`fallback::${item.role}`, item.deviceId)
  }

  const routingChangedCount = (() => {
    const keys = new Set([
      ...Array.from(initialRoutingMap.keys()),
      ...Array.from(currentRoutingMap.keys()),
    ])
    let changed = 0
    for (const key of keys) {
      if (initialRoutingMap.get(key) !== currentRoutingMap.get(key)) {
        changed += 1
      }
    }
    return changed
  })()

  const missingRequiredRoutingCount = (() => {
    let missing = 0
    for (const kiosk of normalizedKiosks) {
      for (const role of ['payment.card', 'printer.receipt'] as const) {
        const stationKey = `${kiosk.stationId}::${role}`
        const zoneKey = `${kiosk.zone}::${role}`
        const assignedDeviceId =
          routingStationMap.get(stationKey) ??
          routingZoneMap.get(zoneKey) ??
          routingFallbackMap.get(role)
        if (!assignedDeviceId) missing += 1
      }
    }
    return missing
  })()

  // ── Mutation helpers ──

  function setStationAssignment(
    stationId: string,
    role: EdgeRoutingRole,
    deviceId: string,
  ) {
    onSetDeviceRouting((prev) => {
      const next = prev.stationAssignments.filter(
        (item) => !(item.stationId === stationId && item.role === role),
      )
      const trimmed = deviceId.trim()
      if (trimmed) next.push({ stationId, role, deviceId: trimmed })
      return { ...prev, stationAssignments: next }
    })
  }

  function setZoneDefault(
    zone: string,
    role: EdgeRoutingRole,
    deviceId: string,
  ) {
    onSetDeviceRouting((prev) => {
      const next = prev.zoneDefaults.filter(
        (item) => !(item.zone === zone && item.role === role),
      )
      const trimmed = deviceId.trim()
      if (trimmed) next.push({ zone, role, deviceId: trimmed })
      return { ...prev, zoneDefaults: next }
    })
  }

  function setFallback(role: EdgeRoutingRole, deviceId: string) {
    onSetDeviceRouting((prev) => {
      const next = prev.fallbacks.filter((item) => item.role !== role)
      const trimmed = deviceId.trim()
      if (trimmed) next.push({ role, deviceId: trimmed })
      return { ...prev, fallbacks: next }
    })
  }

  // ── Device select reused in each card ──

  function DeviceSelect({
    role,
    value,
    onChange,
  }: {
    role: EdgeRoutingRole
    value: string
    onChange: (deviceId: string) => void
  }) {
    const options = assignableDevicesByRole[role]
    return (
      <Select
        value={value || 'none'}
        onValueChange={(v) => onChange(v === 'none' ? '' : v)}
      >
        <SelectTrigger className="w-52">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">
            {tRestaurants('edgeSettings.routing.unassigned')}
          </SelectItem>
          {options.map((device) => (
            <SelectItem key={device.id} value={device.id}>
              {device.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">
        {tRestaurants('edgeSettings.routing.subtitle')}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={routingChangedCount > 0 ? 'default' : 'secondary'}>
          {tRestaurants('edgeSettings.routing.impact.changed', {
            count: routingChangedCount,
          })}
        </Badge>
        <Badge
          variant={
            missingRequiredRoutingCount > 0 ? 'destructive' : 'secondary'
          }
        >
          {tRestaurants('edgeSettings.routing.impact.missing', {
            count: missingRequiredRoutingCount,
          })}
        </Badge>
        {unsavedDeviceCount > 0 ? (
          <Badge variant="outline">
            {tRestaurants('edgeSettings.routing.impact.unsavedDevices', {
              count: unsavedDeviceCount,
            })}
          </Badge>
        ) : null}
      </div>

      {/* ── One card per role ── */}
      <div className="grid gap-4 lg:grid-cols-2">
        {ROUTING_ROLES.map((role) => {
          const options = assignableDevicesByRole[role]
          const isRequired = REQUIRED_ROLES.has(role)
          const hasDevices = options.length > 0

          return (
            <Card key={role}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  {tRestaurants(ROUTING_ROLE_LABEL_KEYS[role])}
                  {isRequired && (
                    <Badge variant="outline" className="text-xs font-normal">
                      {tRestaurants('edgeSettings.routing.required')}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {!hasDevices ? (
                  <p className="text-sm text-muted-foreground">
                    {tRestaurants('edgeSettings.routing.noDevices')}
                  </p>
                ) : (
                  <>
                    {/* Station assignments */}
                    <div className="space-y-3">
                      <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                        {tRestaurants('edgeSettings.routing.stations')}
                      </Label>
                      <div className="space-y-2">
                        {normalizedKiosks.map((kiosk) => (
                          <div
                            key={`${role}-station-${kiosk.stationId}`}
                            className="flex items-center gap-3"
                          >
                            <span
                              className="w-28 truncate text-sm"
                              title={kiosk.stationId}
                            >
                              {kiosk.name || kiosk.stationId}
                            </span>
                            <span className="text-xs text-muted-foreground w-16 truncate">
                              {kiosk.zone}
                            </span>
                            <DeviceSelect
                              role={role}
                              value={
                                routingStationMap.get(
                                  `${kiosk.stationId}::${role}`,
                                ) || ''
                              }
                              onChange={(deviceId) =>
                                setStationAssignment(
                                  kiosk.stationId,
                                  role,
                                  deviceId,
                                )
                              }
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    <Separator />

                    {/* Zone defaults */}
                    <div className="space-y-3">
                      <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                        {tRestaurants('edgeSettings.routing.zoneDefaults')}
                      </Label>
                      <div className="space-y-2">
                        {zoneOptions.map((zone) => (
                          <div
                            key={`${role}-zone-${zone}`}
                            className="flex items-center gap-3"
                          >
                            <span className="w-28 truncate text-sm">
                              {zone}
                            </span>
                            <DeviceSelect
                              role={role}
                              value={
                                routingZoneMap.get(`${zone}::${role}`) || ''
                              }
                              onChange={(deviceId) =>
                                setZoneDefault(zone, role, deviceId)
                              }
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    <Separator />

                    {/* Fallback */}
                    <div className="flex items-center gap-3">
                      <Label className="text-xs text-muted-foreground uppercase tracking-wide w-28">
                        {tRestaurants('edgeSettings.routing.fallbackLabel')}
                      </Label>
                      <DeviceSelect
                        role={role}
                        value={routingFallbackMap.get(role) || ''}
                        onChange={(deviceId) => setFallback(role, deviceId)}
                      />
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  )
}
